import {ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal} from '@angular/core';
import {RequestService} from '../services/request.service';
import {RequestConverterService} from '../services/request-converter.service';
import {RequestTypeDto} from '@dto/requests/request-type.dto';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Router} from '@angular/router';
import {APP_REQUEST} from '../../config/app-urls';
import {LoadingAnimationComponent} from '@shared/components/loading-animation/loading-animation.component';

@Component({
    selector: 'app-request-types',
    templateUrl: './request-types.component.html',
    styleUrls: ['./request-types.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [LoadingAnimationComponent]
})
export class RequestTypesComponent implements OnInit {

  private readonly destroyRef = inject(DestroyRef);
  private readonly requestService = inject(RequestService);
  private readonly requestConverterService = inject(RequestConverterService);
  private readonly router = inject(Router);

  public readonly dataLoading = signal(true);
  public readonly dataAvailable = signal(false);
  public readonly dataEmpty = signal(false);
  public readonly errorMessage = signal<string | null>(null);
  public readonly requestTypes = signal<RequestTypeDto[]>([]);

  ngOnInit(): void {
    this.getRequestTypes();
  }

  getRequestTypes(): void {
    this.dataLoading.set(true);
    this.errorMessage.set(null);
    this.requestService.getRequestTypes().pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: requestTypes => {
        this.requestTypes.set(requestTypes.requestTypes);
        this.dataLoading.set(false);
        this.dataEmpty.set(this.requestTypes().length === 0);
        this.dataAvailable.set(!this.dataEmpty());
      },
      error: () => {
        this.dataLoading.set(false);
        this.dataEmpty.set(true);
        this.dataAvailable.set(false);
        this.errorMessage.set('Failed to load request types. Please try again later.');
      }
    });
  }

  initiateRequest(requestTypeId: string): void {
    this.dataLoading.set(true);
    this.dataEmpty.set(false);
    this.dataAvailable.set(false);
    this.errorMessage.set(null);

    this.requestService.initiateRequest(requestTypeId).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (initiateRequest) => {
        const summary = this.requestConverterService.convertInitiatedRequestDtoToRequestViewModel(initiateRequest);
        this.router.navigate(
          APP_REQUEST.toRouterLinkCommands(initiateRequest.requestId),
          { state: { taskDetailViewModel: summary } }
        );
      },
      error: () => {
        this.dataLoading.set(false);
        this.dataEmpty.set(this.requestTypes().length === 0);
        this.dataAvailable.set(!this.dataEmpty());
        this.errorMessage.set('Failed to start the request. Please try again later.');
      }
    });
  }

  dismissError(): void {
    this.errorMessage.set(null);
  }
}
